import React, {useState, useEffect, useRef} from 'react';
import axios from "axios";
import SingleDetail from './single/SingleDetail';
import SignupStart from './single/SignupStart';
import SignupDate from './single/SignupDate';
import SignupQuestions from './single/SignupQuestions';
import SignupContact from './single/SignupContact';
import SignupConfirm from './single/SignupConfirm';
import SignupEnd from './single/SignupEnd';
import {setStructuredSingleData} from "./schema";

const Single = ({ client, apartmentID, clientUrl }) => {
	const [isLoading, setIsLoading] = useState(true);
	const [authToken, setAuthToken] = useState('');
	const [err, setErr] = useState([]);
	const [apartment, setApartment] = useState([]);
	const [viewings, setViewings] = useState([]);
	const [step, setStep] = useState(0);
	const [dates, setDates] = useState([]);
	const [reason, setReason] = useState('');
	const [gwgMember, setGwgMember] = useState('');
	const [peopleCount, setPeopleCount] = useState(1);
	const [recommendedIncome, setRecommendedIncome] = useState('');
	const [hasCats, setHasCats] = useState('');
	const [comment, setComment] = useState('');
	const [firstname, setFirstname] = useState('');
	const [lastname, setLastname] = useState('');
	const [street, setStreet] = useState('');
	const [streetNr, setStreetNr] = useState('');
	const [zip, setZip] = useState('');
	const [place, setPlace] = useState('');
	const [email, setEmail] = useState('');
	const [phone, setPhone] = useState('');
	const [flatmates, setFlatmates] = useState({});
	const focusedTitleRef = useRef(null);

	useEffect(() => {
		fireQuery();
	}, []);

	useEffect(() => {
		scrollTop();
	}, [step, err]);

	useEffect(() => {
		if(step !== 0) {
			focusedTitleRef.current.focus();
		}
	}, [step, err]);

	const scrollTop = () => {
		const element = document.getElementById("af_single");
		const header = document.querySelector('.header');
		let y = element.offsetTop - header.offsetHeight * 2;
		window.scrollTo({
			top: y,
			behavior: 'smooth'
		});
	};

	const prevStep = () => setStep(step - 1);

	const nextStep = () => {
		if (validateStep(step)) {
			setStep(step + 1);
		}
	};

	const handleChange = input => e => {
		const value = e.target.value;
		if (input.includes("flatmates")) {
			const [, flatmateIndex, field] = input.split("_");
			const updatedFlatmates = { ...flatmates };
			if (!updatedFlatmates[parseFloat(flatmateIndex)]) {
				updatedFlatmates[parseFloat(flatmateIndex)] = {};
			}
			updatedFlatmates[parseFloat(flatmateIndex)][field] = value;
			setFlatmates(updatedFlatmates);
		} else if (input.includes("peopleCount")) {
			const updatedFlatmates = { ...flatmates };
			for (let i = 10; i >= value; i--) {
				if (updatedFlatmates[i] !== undefined) {
					delete updatedFlatmates[i];
				}
			}
			setPeopleCount(value);
			setFlatmates(updatedFlatmates);
		} else if (input.includes("email")) {
			setEmail(value.trim());
		} else {
			switch (input) {
				case 'reason': setReason(value); break;
				case 'gwgMember': setGwgMember(value); break;
				case 'recommendedIncome': setRecommendedIncome(value); break;
				case 'hasCats': setHasCats(value); break;
				case 'comment': setComment(value); break;
				case 'firstname': setFirstname(value); break;
				case 'lastname': setLastname(value); break;
				case 'street': setStreet(value); break;
				case 'street_nr': setStreetNr(value); break;
				case 'zip': setZip(value); break;
				case 'place': setPlace(value); break;
				case 'phone': setPhone(value); break;
				default: break;
			}
		}
	};

	const validateEmail = (email) => {
		return email.match(
			/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
		);
	};

	const validateStep = (step) => {
		let errors = [];
		switch (step) {
			case 2:
				if (dates.length === 0) {
					errors.push('Bitte wählen Sie einen oder mehrere Termine aus');
				}
				break;
			case 3:
				if (reason === '') {
					errors.push('Bitte geben Sie ein, wieso Sie eine neue Wohnung suchen.');
				}
				if (gwgMember === '') {
					errors.push('Bitte geben Sie an, ob Sie schon bei der GWG wohnen.');
				}
				if (peopleCount < apartment.meta?.minResidents) {
					errors.push(`In dieser Wohnung müssen mindestens ${apartment.meta?.minResidents === 1 ? '1 Person' : `${apartment.meta?.minResidents} Personen`} leben`);
				}
				if (recommendedIncome === '') {
					errors.push('Bitte geben Sie an, ob Ihr Bruttohaushaltseinkommen über der Empfehlung liegt.');
				}
				if (hasCats === '') {
					errors.push('Bitte geben Sie an, ob Sie eine Katze besitzen.');
				}
				if (hasCats === 'outdoors') {
					if (apartment.meta?.cats?.[0] === 'no') {
						errors.push('In dieser Wohnung ist die Haltung von Katzen nicht möglich');
					} else if (apartment.meta?.cats?.[0] === 'indoors') {
						errors.push('In dieser Wohnung ist die Haltung von Katzen mit Auslauf nicht möglich');
					}
				} else if (hasCats === 'indoors') {
					if (apartment.meta?.cats?.[0] === 'no') {
						errors.push('In dieser Wohnung ist die Haltung von Katzen nicht möglich');
					}
				}
				break;
			case 4:
				if (firstname === '') {
					errors.push('Bitte geben Sie Ihren Vornamen ein.');
				}
				if (lastname === '') {
					errors.push('Bitte geben Sie Ihren Nachnamen ein.');
				}
				if (street === '') {
					errors.push('Bitte geben Sie Ihre Strasse ein.');
				}
				if (streetNr === '') {
					errors.push('Bitte geben Sie Ihre Strassennummer ein.');
				}
				if (zip === '') {
					errors.push('Bitte geben Sie Ihre Postleitzahl ein.');
				}
				if (place === '') {
					errors.push('Bitte geben Sie Ihren Wohnort ein.');
				}
				if (phone === '') {
					errors.push('Bitte geben Sie Ihre Telefonnummer ein.');
				}
				if (email === '') {
					errors.push('Bitte geben Sie Ihre Mailadresse ein.');
				}
				if (!validateEmail(email)) {
					errors.push('Bitte geben Sie eine gültige Mailadresse ein.');
				}
				for (let i = 1; i < peopleCount; i++) {
					if (flatmates[i] === undefined || flatmates[i]['firstname'] === undefined || flatmates[i]['firstname'] === '') {
						errors.push('Bitte geben Sie den Namen der Person(en) an, die mit Ihnen in die Wohnungen ziehen sollen.');
						break;
					}
					if (flatmates[i] === undefined || flatmates[i]['lastname'] === undefined || flatmates[i]['lastname'] === '') {
						errors.push('Bitte geben Sie den Namen der Person(en) an, die mit Ihnen in die Wohnungen ziehen sollen.');
						break;
					}
				}
				break;
		}
		if (errors.length > 0) {
			setErr(errors);
			return false;
		} else {
			setErr(errors);
			return true;
		}
	};

	const handleCheckboxChange = input => e => {
		let updatedDates = [...dates];
		let find = updatedDates.indexOf(e.target.value);
		if (find > -1) {
			updatedDates.splice(find, 1);
		} else {
			updatedDates.push(e.target.value);
		}
		setDates(updatedDates);
	};

	const fireQuery = () => {
		axios({
			url: client,
			method: 'POST',
			data: {
				query: `
                    query apartmentQuery($id: Int!) {
                      apartmentBy(apartmentId: $id) {
                        id
                        apartmentId
                        title
                        meta {
                          address
                          apartmentType
                          availabilityDate
                          availableByAgreement
                          cats
                          city
                          files {
                            label
                            file {
                                node {
                                    title
                                    mediaItemUrl
                                }
                            }
                          }
                          flatFit
                          flatHighlights
                          floor
                          minResidents
                          numberOfRooms
                          objectId
                          registrationEnds
                          registrationOpen
                          anteilscheinkapital
                          rentPerMonth
                          settlement
                          settlementLink
                          surfaceArea
                          zip
                          thumbnailId {
                            node {
                                title
                                mediaItemUrl
                            }
                          }
                          headerImage {
                            node {
                                title
                                mediaItemUrl
                            }
                          }
                        }
                      }
                    }
                `,
				variables: {
					id: apartmentID,
				}
			}
		}).then((result) => {
			setApartment(result.data.data.apartmentBy);
			setStructuredSingleData(result.data.data.apartmentBy);
			axios({
				url: client,
				method: 'POST',
				data: {
					query: `
                        query viewingQuery($value: String) {
                            viewings(where: {orderby: {field: TITLE, order: ASC}, metaQuery: {metaArray: {key: "apartment", value: $value}}}) {
                                nodes {
                                  viewingId
                                  viewingMeta {
                                    dateEnd
                                    dateStart
                                  }
                                }
                            }
                        }
                    `,
					variables: {
						value: apartmentID.toString(),
					}
				}
			}).then((result) => {
				setViewings(result.data.data.viewings.nodes);
				setIsLoading(false);
			});
		});
	};

	const endForm = () => {
		setStep(6);
		axios({
			url: clientUrl + '/wp-json/apartment-finder/v1/registrations',
			method: 'POST',
			data: {
				dates,
				reason,
				gwgMember,
				peopleCount,
				recommendedIncome,
				hasCats,
				comment,
				firstname,
				lastname,
				street,
				street_nr: streetNr,
				zip,
				place,
				email,
				phone,
				flatmates,
			}
		}).then(() => {
			setDates([]);
			setReason('');
			setGwgMember('');
			setPeopleCount(1);
			setRecommendedIncome('');
			setHasCats('');
			setComment('');
			setFirstname('');
			setLastname('');
			setStreet('');
			setStreetNr('');
			setZip('');
			setPlace('');
			setEmail('');
			setPhone('');
			setFlatmates({});
		});
	};

	const values = { dates, reason, gwgMember, peopleCount, recommendedIncome, hasCats, comment, firstname, lastname, street, street_nr: streetNr, zip, place, email, phone, flatmates };

	switch (step) {
		case 0:
			return (
				<SingleDetail
					err={err}
					apartment={apartment}
					prevStep={prevStep}
					nextStep={nextStep}
					setStep={setStep}
					handleChange={handleChange}
					isLoading={isLoading}
					values={values}
					focusedTitleRef={focusedTitleRef}
				/>
			);
		case 1:
			return (
				<SignupStart
					err={err}
					apartment={apartment}
					prevStep={prevStep}
					nextStep={nextStep}
					setStep={setStep}
					handleChange={handleChange}
					values={values}
					focusedTitleRef={focusedTitleRef}
				/>
			);
		case 2:
			return (
				<SignupDate
					err={err}
					apartment={apartment}
					viewings={viewings}
					prevStep={prevStep}
					nextStep={nextStep}
					setStep={setStep}
					handleChange={handleChange}
					handleCheckboxChange={handleCheckboxChange}
					validateStep={validateStep}
					values={values}
					focusedTitleRef={focusedTitleRef}
				/>
			);
		case 3:
			return (
				<SignupQuestions
					err={err}
					apartment={apartment}
					prevStep={prevStep}
					nextStep={nextStep}
					setStep={setStep}
					handleChange={handleChange}
					validateStep={validateStep}
					values={values}
					focusedTitleRef={focusedTitleRef}
				/>
			);
		case 4:
			return (
				<SignupContact
					err={err}
					apartment={apartment}
					prevStep={prevStep}
					nextStep={nextStep}
					setStep={setStep}
					handleChange={handleChange}
					validateStep={validateStep}
					values={values}
					focusedTitleRef={focusedTitleRef}
				/>
			);
		case 5:
			return (
				<SignupConfirm
					err={err}
					apartment={apartment}
					viewings={viewings}
					prevStep={prevStep}
					nextStep={nextStep}
					setStep={setStep}
					handleChange={handleChange}
					validateStep={validateStep}
					endForm={endForm}
					values={values}
					focusedTitleRef={focusedTitleRef}
				/>
			);
		case 6:
			return (
				<SignupEnd
					err={err}
					apartment={apartment}
					prevStep={prevStep}
					nextStep={nextStep}
					setStep={setStep}
					handleChange={handleChange}
					values={values}
					focusedTitleRef={focusedTitleRef}
				/>
			);
		default:
			return null;
	}
};

export default Single;
